'use client';

import { useEffect, useRef, useState } from 'react';

const CHARS = '0123456789';
const DURATION_MS = 1400;
const STAGGER_MS = 80;

// blue-600: #2563eb  →  sky-400: #38bdf8
const FROM_COLOR: [number, number, number] = [37, 99, 235];
const TO_COLOR: [number, number, number] = [56, 189, 248];

function lerp(a: number, b: number, t: number) {
  return Math.round(a + (b - a) * t);
}

function sliceGradient(index: number, total: number): string {
  const t0 = index / total;
  const t1 = (index + 1) / total;
  const c0 = `rgb(${lerp(FROM_COLOR[0], TO_COLOR[0], t0)},${lerp(FROM_COLOR[1], TO_COLOR[1], t0)},${lerp(FROM_COLOR[2], TO_COLOR[2], t0)})`;
  const c1 = `rgb(${lerp(FROM_COLOR[0], TO_COLOR[0], t1)},${lerp(FROM_COLOR[1], TO_COLOR[1], t1)},${lerp(FROM_COLOR[2], TO_COLOR[2], t1)})`;
  return `linear-gradient(to right, ${c0}, ${c1})`;
}

function buildColumn(target: string): string[] {
  return [target, ...CHARS.split(''), target];
}

function SlotChar({
  char,
  index,
  total,
  animate,
}: {
  char: string;
  index: number;
  total: number;
  animate: boolean;
}) {
  const isDigit = CHARS.includes(char);
  const column = isDigit ? buildColumn(char) : [char];
  const columnTotal = column.length;

  const gradientStyle = {
    background: sliceGradient(index, total),
    WebkitBackgroundClip: 'text' as const,
    backgroundClip: 'text' as const,
    color: 'transparent' as const,
    lineHeight: 'inherit',
  };

  return (
    <span className="inline-block overflow-hidden" style={{ lineHeight: 'inherit', height: '1lh' }}>
      <span
        className="inline-flex flex-col"
        style={{
          transform:
            animate && isDigit
              ? `translateY(-${((columnTotal - 1) / columnTotal) * 100}%)`
              : 'translateY(0)',
          transition:
            animate && isDigit
              ? `transform ${DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1) ${index * STAGGER_MS}ms`
              : 'none',
        }}
      >
        {column.map((c, i) => (
          <span key={i} className="block" style={gradientStyle}>
            {c}
          </span>
        ))}
      </span>
    </span>
  );
}

export function SlideValue({ value, runOnce = false }: { value: string; runOnce?: boolean }) {
  const [animate, setAnimate] = useState(false);
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (runOnce && entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        } else {
          setAnimate(entry.isIntersecting);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const chars = value.split('');

  return (
    <h2
      ref={ref}
      className="font-sans font-bold text-[64px] leading-[110%] @lg:text-[128px] @lg:leading-[120%] text-center tracking-[-0.03em]"
      style={{ height: '1lh' }}
    >
      {chars.map((char, i) => (
        <SlotChar key={i} char={char} index={i} total={chars.length} animate={animate} />
      ))}
    </h2>
  );
}

export default SlideValue;
